import * as React from 'react';
import {
  Flex,
  VisuallyHiddenInput,
  Progress,
  Button,
  Tooltip,
  IconButton,
  useDisclosure,
  Box,
  Badge,
  ButtonProps,
  BoxProps,
  Text
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCheck, faFileArrowUp, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';

import sanitize from 'sanitize-s3-objectkey';
import { UploadFileStatus } from '../../../types';

import { ALLOWED_FILE_TYPES } from '../../../utilities/constants';
import ConfirmationModal from '../modals-v2';

interface IProps {
  id?: string;
  name?: string;
  buttonProps?: ButtonProps;
  fileName?: string;
  fileStatus?: string;
  progress?: number;
  labelText?: string;
  allUploads?: Array<string>;
  fileTypes?: string;
  hasProgressBar?: boolean;
  onChange: (fileName: string, file: File) => void;
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FormEvent<HTMLInputElement>) => void;
  onDelete?: (fileName: string) => void;
  mb?: BoxProps['marginBottom'];
  isDisabled?: boolean;
  maxFileSize?: number;
  warningText?: string;
}

const FileUpload: React.FC<IProps> = (props: IProps) => {
  const {
    allUploads,
    onChange,
    fileStatus = 'Ready',
    id = 'enroly',
    name = 'enroly',
    fileTypes,
    onBlur,
    onFocus,
    progress = 0,
    fileName = 'No file chosen',
    onDelete,
    hasProgressBar,
    labelText = 'Choose file',
    buttonProps,
    mb = 3,
    isDisabled = false,
    maxFileSize,
    warningText
  } = props;

  const KB = 1024;
  const MB = 1048576;

  const [sizeError, setSizeError] = React.useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < KB) return bytes + ' bytes';
    else if (bytes < MB) return (bytes / KB).toFixed(1) + ' KB';
    else return (bytes / MB).toFixed(1) + ' MB';
  };

  const handleFileUpload = (evt: React.ChangeEvent<HTMLInputElement>) => {
    evt.preventDefault();
    const { files } = evt.target;
    setSizeError(null);

    if (!files.length) {
      return;
    }

    // Check file size if maxFileSize is specified
    if (maxFileSize && files[0].size > maxFileSize) {
      setSizeError(
        warningText || 
        `File size exceeds the maximum limit of ${formatFileSize(maxFileSize)}. Selected file is ${formatFileSize(files[0].size)}.`
      );
      evt.target.value = '';
      return;
    }

    const fullFileName = evt.target.value;
    let fileName = '';
    if (fullFileName) {
      const startIndex =
        fullFileName.indexOf('\\') >= 0 ? fullFileName.lastIndexOf('\\') : fullFileName.lastIndexOf('/');
      fileName = fullFileName.substring(startIndex);
      if (fileName.indexOf('\\') === 0 || fileName.indexOf('/') === 0) {
        fileName = fileName.substring(1);
      }
      // NB! sanitise filename for AWS
      fileName = sanitize(fileName);
    }
    const fName = fileName.substring(0, fileName.lastIndexOf('.'));
    const fNameNoDots = fName.split('.').join('-');

    const ext = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length)?.toLowerCase();

    fileName = `${fNameNoDots}.${ext}`; // NB! handle any case in file extension

    if (allUploads?.includes(fileName)) {
      let i = 1;
      do {
        fileName = `${fNameNoDots} (${i}).${ext}`;
        i++;
      } while (allUploads?.includes(fileName));
    }
    onChange(fileName, files[0]);
  };

  const disabled = isDisabled || (progress && progress > 0 && progress < 100 ? true : false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const deleteFile = (e: React.MouseEvent) => {
    e.preventDefault();
    onOpen();
  };

  const fileInputRef = React.useRef(null);

  const badgeProps = () => {
    if (fileStatus === UploadFileStatus.ERROR) {
      return { colorScheme: 'red', children: 'Error' };
    } else if (fileStatus === UploadFileStatus.UPLOADING) {
      return { colorScheme: 'blue', children: 'Uploading' };
    } else if (fileStatus === UploadFileStatus.OK) {
      return { colorScheme: 'green', children: 'Uploaded' };
    } else {
      return { colorScheme: 'gray' };
    }
  };

  const progressColor = () => {
    if (fileStatus === UploadFileStatus.ERROR) {
      return 'red';
    } else if (fileStatus === UploadFileStatus.UPLOADING) {
      return 'blue';
    } else if (fileStatus === UploadFileStatus.OK) {
      return 'green';
    } else {
      return null;
    }
  };

  return (
    <Box id={`${id}-file-upload`} mb={mb}>
      <VisuallyHiddenInput
        type="file"
        name={name}
        aria-label="Choose file"
        accept={fileTypes ? fileTypes : ALLOWED_FILE_TYPES.map(s => `.${s}`).join(', ')}
        id={`${id}-file-input`}
        onChange={handleFileUpload}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        tabIndex={-1}
        ref={fileInputRef}
      />

      {progress > 0 ? (
        <Flex
          flexDirection={{ base: 'column', sm: 'row' }}
          rowGap={1}
          borderWidth={1}
          borderBottomWidth={0}
          borderRadius="lg"
          bgColor={'chakra-body-bg'}
          position="relative"
          overflow="hidden"
          pl={4}
          pr={2}
          pt={2.5}
          pb={3}
          minHeight={12}
        >
          <Flex data-selector={`${id}-file-name`} flexGrow="1">
            <Box as="span" mr={2} display={{ base: 'none', sm: 'inline' }}>
              <FontAwesomeIcon
                icon={progress === 100 ? faFileCheck : faFileArrowUp}
                className="utility-icon"
                fixedWidth
              />
            </Box>

            <Box flexShrink="1" wordBreak="break-word">
              {fileName}
            </Box>
          </Flex>

          <Box flexShrink="0">
            <Badge data-selector={`${id}-file-status`} fontWeight="medium" mr={2} {...badgeProps()} />
          </Box>

          {onDelete && progress && progress === 100 ? (
            <Tooltip hasArrow placement="top" label="Delete file">
              <IconButton
                aria-label="Delete file"
                onClick={deleteFile}
                data-selector={`${id}-file-delete`}
                icon={<FontAwesomeIcon icon={faTimesCircle} />}
                variant="ghost"
                colorScheme="gray"
                my={-2}
              />
            </Tooltip>
          ) : null}

          {hasProgressBar !== false ? (
            <Progress
              aria-label="Progress indicator"
              data-selector={`${id}-file-progress`}
              value={progress ? progress : 0}
              flexGrow="1"
              height={1}
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              colorScheme={progressColor()}
            />
          ) : null}
        </Flex>
      ) : (
        <Flex flexDirection="column">
          <Flex alignItems="center">
            <Button
              {...buttonProps}
              data-selector={`${id}-file-label`}
              onClick={() => fileInputRef.current.click()}
              isDisabled={disabled}
              size={buttonProps?.size ?? 'lg'}
              variant={buttonProps?.variant ?? 'solid'}
              colorScheme={buttonProps?.colorScheme ?? 'blue'}
            >
              {labelText}
            </Button>

            {fileName ? (
              <Box data-selector={`${id}-file-name`} color="muted" flexGrow="1" ml={3}>
                {fileName}
              </Box>
            ) : null}
          </Flex>
          
          {sizeError && (
            <Flex alignItems="center" color="red.500" mt={2} data-selector={`${id}-file-error`}>
              <Text ml={2} fontSize="sm">{sizeError}</Text>
            </Flex>
          )}
        </Flex>
      )}

      <ConfirmationModal
        id="deleteDocument"
        isOpen={isOpen}
        titleText={'Permanently Delete File?'}
        confirmationButtonText={'Confirm'}
        isDestructive={true}
        onClickSuccess={() => {
          onDelete && fileName && onDelete(props.fileName);
          onClose();
        }}
        onClose={onClose}
      />
    </Box>
  );
};

export default FileUpload;
